import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  Circle,
  FileText,
  Layers,
  Pencil,
  Plus,
  Search,
  Trash2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { canEditOwn, useAuthStore } from "@/features/auth/store";
import {
  useCreateDocument,
  useDeleteDocument,
  useDocumentChunks,
  useDocuments,
  useKnowledgeBase,
  useSearchDocuments,
  useUpdateDocument,
  useUploadDocument,
} from "@/features/knowledge-bases/hooks";
import type {
  Document,
  DocumentChunk,
} from "@/features/knowledge-bases/api";
import { cn } from "@/lib/utils";

export default function KnowledgeBaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role ?? "member");
  // Customers own everything they can see here, so rank does not apply.
  const canEdit = canEditOwn(role);

  const kbQ = useKnowledgeBase(id);
  const docsQ = useDocuments(id);

  const [addOpen, setAddOpen] = useState(false);
  const [viewDoc, setViewDoc] = useState<Document | null>(null);
  const [chunksDoc, setChunksDoc] = useState<Document | null>(null);
  const [deleteDoc, setDeleteDoc] = useState<Document | null>(null);

  const docs = docsQ.data ?? [];

  if (kbQ.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-1/3" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (kbQ.isError || !kbQ.data) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/knowledge-bases">
            <ArrowLeft className="size-4" />
            Back to Knowledge Bases
          </Link>
        </Button>
        <Card>
          <CardContent className="py-10 text-center text-sm text-destructive">
            Failed to load knowledge base.
          </CardContent>
        </Card>
      </div>
    );
  }

  const kb = kbQ.data;

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/knowledge-bases")}
          aria-label="Back"
          className="mt-1"
        >
          <ArrowLeft className="size-5" />
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <BookOpen className="size-5 text-indigo-500" />
            <h1 className="truncate text-2xl font-semibold tracking-tight">
              {kb.name}
            </h1>
          </div>
          {kb.description && (
            <p className="mt-1 text-sm text-muted-foreground">
              {kb.description}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Chip>{kb.document_count} documents</Chip>
          <Chip>{kb.chunk_count} chunks</Chip>
        </div>
      </div>

      <SearchCard kbId={kb.id} />

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Documents</h2>
        {canEdit && (
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="size-4" />
            Add Document
          </Button>
        )}
      </div>

      {docsQ.isLoading && <DocumentsTableSkeleton />}

      {!docsQ.isLoading && docs.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <FileText className="size-8 text-muted-foreground" />
            <div>
              <p className="text-base font-medium">No Documents</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Add documents so your agents can use them.
              </p>
            </div>
            {canEdit && (
              <Button variant="outline" onClick={() => setAddOpen(true)}>
                <Plus className="size-4" />
                Add Your First Document
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {!docsQ.isLoading && docs.length > 0 && (
        <DocumentsTable
          docs={docs}
          canEdit={canEdit}
          onView={setViewDoc}
          onChunks={setChunksDoc}
          onDelete={setDeleteDoc}
        />
      )}

      {addOpen && (
        <AddDocumentDialog
          kbId={kb.id}
          open={addOpen}
          onOpenChange={setAddOpen}
        />
      )}
      <ViewDocumentDialog
        kbId={kb.id}
        canEdit={canEdit}
        doc={viewDoc}
        onClose={() => setViewDoc(null)}
      />
      <ChunksDialog
        doc={chunksDoc}
        onClose={() => setChunksDoc(null)}
      />
      <DeleteDocumentConfirm
        kbId={kb.id}
        doc={deleteDoc}
        onClose={() => setDeleteDoc(null)}
      />
    </div>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border border-input bg-background px-2 py-0.5 text-xs text-muted-foreground">
      {children}
    </span>
  );
}

function DocumentsTableSkeleton() {
  return (
    <Card>
      <CardContent className="p-0">
        <div className="space-y-2 p-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function DocumentsTable({
  docs,
  canEdit,
  onView,
  onChunks,
  onDelete,
}: {
  docs: Document[];
  canEdit: boolean;
  onView: (d: Document) => void;
  onChunks: (d: Document) => void;
  onDelete: (d: Document) => void;
}) {
  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">Title</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Chunks</th>
                <th className="px-4 py-3 font-medium">Source URL</th>
                <th className="px-4 py-3 font-medium">Added</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {docs.map((d) => (
                <tr key={d.id} className="hover:bg-accent/40">
                  <td className="px-4 py-2.5">
                    <button
                      type="button"
                      onClick={() => onView(d)}
                      className="inline-flex items-center gap-2 text-left font-medium hover:text-primary"
                    >
                      <FileText className="size-4 text-muted-foreground" />
                      <span className="truncate">{d.title}</span>
                    </button>
                  </td>
                  <td className="px-4 py-2.5">
                    <Chip>{d.content_type || "text"}</Chip>
                  </td>
                  <td className="px-4 py-2.5 tabular-nums">
                    {d.chunk_count ?? 0}
                  </td>
                  <td className="max-w-[200px] truncate px-4 py-2.5">
                    {d.source_url ? (
                      <span className="text-primary">{d.source_url}</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground">
                    {d.created_at
                      ? new Date(d.created_at).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex justify-end gap-1">
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8"
                          onClick={() => onChunks(d)}
                          aria-label={`View chunks of ${d.title}`}
                        >
                          <Layers className="size-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        onClick={() => onView(d)}
                        aria-label={canEdit ? `Edit ${d.title}` : `View ${d.title}`}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8 text-destructive hover:text-destructive"
                          onClick={() => onDelete(d)}
                          aria-label={`Delete ${d.title}`}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function SearchCard({ kbId }: { kbId: string }) {
  const [query, setQuery] = useState("");
  const [touched, setTouched] = useState(false);
  const search = useSearchDocuments(kbId);
  const results = search.data ?? [];

  function go() {
    if (!query.trim()) return;
    setTouched(true);
    search.mutate(query.trim());
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Search documents</CardTitle>
        <CardDescription>
          Semantic search across this knowledge base.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  go();
                }
              }}
              placeholder="Ask a question or search across documents…"
              className="pl-8"
            />
          </div>
          <Button onClick={go} disabled={!query.trim() || search.isPending}>
            {search.isPending ? "Searching…" : "Search"}
          </Button>
        </div>

        {search.isError && (
          <p className="text-sm text-destructive">Search failed.</p>
        )}

        {touched && !search.isPending && !search.isError && (
          <SearchResults results={results} />
        )}
      </CardContent>
    </Card>
  );
}

function SearchResults({ results }: { results: DocumentChunk[] }) {
  if (results.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No matching chunks found.</p>
    );
  }
  return (
    <ul className="space-y-2">
      {results.map((r, idx) => (
        <li
          key={r.id ?? idx}
          className="rounded-md border bg-muted/40 p-3 text-sm"
        >
          <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
            {r.document_title && (
              <span className="font-medium text-foreground">
                {r.document_title}
              </span>
            )}
            <Chip>#{r.chunk_index}</Chip>
            {typeof r.similarity === "number" && (
              <span className="tabular-nums">
                {(r.similarity * 100).toFixed(0)}% match
              </span>
            )}
          </div>
          <p className="whitespace-pre-wrap text-sm text-foreground/90">
            {r.content.length > 400
              ? r.content.slice(0, 400) + "…"
              : r.content}
          </p>
        </li>
      ))}
    </ul>
  );
}

function AddDocumentDialog({
  kbId,
  open,
  onOpenChange,
}: {
  kbId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const [mode, setMode] = useState<"text" | "file">("text");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState("");

  const createDoc = useCreateDocument(kbId);
  const uploadDoc = useUploadDocument(kbId);
  const pending = createDoc.isPending || uploadDoc.isPending;

  function reset() {
    setMode("text");
    setTitle("");
    setContent("");
    setSourceUrl("");
    setFile(null);
    setError("");
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    try {
      if (mode === "file") {
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        fd.append("knowledge_base", kbId);
        if (title.trim()) fd.append("title", title.trim());
        await uploadDoc.mutateAsync(fd);
      } else {
        if (!title.trim() || !content.trim()) return;
        await createDoc.mutateAsync({
          title: title.trim(),
          content,
          source_url: sourceUrl.trim() || undefined,
          content_type: "text",
        });
      }
      toast.success("Document added — chunking & embedding in background.");
      reset();
      onOpenChange(false);
    } catch (err) {
      const e = err as {
        response?: {
          data?: {
            error?: string;
            detail?: string;
            non_field_errors?: string[];
          };
        };
      };
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.detail ||
        e?.response?.data?.non_field_errors?.[0] ||
        "Failed to add document.";
      setError(msg);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add Document</DialogTitle>
          <DialogDescription>
            Paste text or upload a file. Content is split into chunks and
            embedded automatically.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="flex gap-2">
          <Button
            type="button"
            variant={mode === "text" ? "default" : "outline"}
            size="sm"
            onClick={() => setMode("text")}
          >
            Paste Text
          </Button>
          <Button
            type="button"
            variant={mode === "file" ? "default" : "outline"}
            size="sm"
            onClick={() => setMode("file")}
          >
            <Upload className="size-4" />
            Upload File
          </Button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "file" ? (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="doc-title-file">
                  Title (optional — defaults to filename)
                </Label>
                <Input
                  id="doc-title-file"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Product FAQ"
                />
              </div>
              <label
                className={cn(
                  "flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed px-4 py-8 text-center transition-colors",
                  file
                    ? "border-primary bg-primary/5"
                    : "border-input hover:bg-accent",
                )}
              >
                <Upload className="size-6 text-muted-foreground" />
                <span className="text-sm font-medium">
                  {file
                    ? file.name
                    : "Choose a file (PDF, DOCX, TXT, MD, CSV)"}
                </span>
                {file && (
                  <span className="text-xs text-muted-foreground">
                    {(file.size / 1024).toFixed(1)} KB — max 10 MB
                  </span>
                )}
                <input
                  type="file"
                  className="sr-only"
                  accept=".pdf,.docx,.txt,.md,.csv"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
              </label>
            </>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="doc-title">Title</Label>
                <Input
                  id="doc-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Product FAQ, Return Policy"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="doc-content">Content</Label>
                <Textarea
                  id="doc-content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={10}
                  placeholder="Paste your document content here…"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  {content
                    ? `${content.length.toLocaleString()} characters`
                    : "Paste the text content for this document"}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="doc-source">Source URL (optional)</Label>
                <Input
                  id="doc-source"
                  type="url"
                  value={sourceUrl}
                  onChange={(e) => setSourceUrl(e.target.value)}
                  placeholder="https://example.com/article"
                />
              </div>
            </>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                pending ||
                (mode === "file" && !file) ||
                (mode === "text" && (!title.trim() || !content.trim()))
              }
            >
              {pending
                ? mode === "file"
                  ? "Uploading…"
                  : "Adding…"
                : mode === "file"
                ? "Upload & Process"
                : "Add Document"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ViewDocumentDialog({
  kbId,
  canEdit,
  doc,
  onClose,
}: {
  kbId: string;
  canEdit: boolean;
  doc: Document | null;
  onClose: () => void;
}) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const update = useUpdateDocument(kbId);

  useEffect(() => {
    if (doc) {
      setTitle(doc.title);
      setContent(doc.content ?? "");
    }
  }, [doc]);

  async function save() {
    if (!doc) return;
    try {
      await update.mutateAsync({
        id: doc.id,
        data: { title: title.trim(), content },
      });
      toast.success("Document updated");
      onClose();
    } catch {
      toast.error("Failed to update document");
    }
  }

  return (
    <Dialog open={!!doc} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {canEdit ? "Edit Document" : "View Document"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="doc-edit-title">Title</Label>
            <Input
              id="doc-edit-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={!canEdit}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="doc-edit-content">Content</Label>
            <Textarea
              id="doc-edit-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={12}
              disabled={!canEdit}
            />
            <p className="text-xs text-muted-foreground">
              {content
                ? `${content.length.toLocaleString()} characters`
                : ""}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {canEdit ? "Cancel" : "Close"}
          </Button>
          {canEdit && (
            <Button
              onClick={save}
              disabled={
                update.isPending || !title.trim() || !content.trim()
              }
            >
              {update.isPending ? "Saving…" : "Save Changes"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChunksDialog({
  doc,
  onClose,
}: {
  doc: Document | null;
  onClose: () => void;
}) {
  const q = useDocumentChunks(doc?.id ?? null);
  const chunks = q.data ?? [];
  const indexed = chunks.filter((c) => c.has_embedding).length;

  return (
    <Dialog open={!!doc} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Chunks — {doc?.title}</DialogTitle>
          {!q.isLoading && doc && (
            <DialogDescription>
              {chunks.length} chunk{chunks.length !== 1 ? "s" : ""} ·{" "}
              {indexed} indexed
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
          {q.isLoading && (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          )}

          {!q.isLoading && chunks.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No chunks found for this document.
            </p>
          )}

          {!q.isLoading &&
            chunks.map((c) => (
              <div
                key={c.id}
                className="rounded-md border bg-muted/30 p-3 text-sm"
              >
                <div className="mb-1.5 flex items-center gap-2 text-xs text-muted-foreground">
                  <Chip>#{c.chunk_index}</Chip>
                  {c.has_embedding ? (
                    <span
                      className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400"
                      title="Embedded — searchable"
                    >
                      <CheckCircle2 className="size-3.5" />
                      indexed
                    </span>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1 text-muted-foreground"
                      title="No embedding yet"
                    >
                      <Circle className="size-3.5" />
                      not indexed
                    </span>
                  )}
                  <span className="tabular-nums">
                    {(c.content?.length ?? 0).toLocaleString()} chars
                  </span>
                </div>
                <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded bg-background p-2 text-xs">
                  {c.content}
                </pre>
              </div>
            ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDocumentConfirm({
  kbId,
  doc,
  onClose,
}: {
  kbId: string;
  doc: Document | null;
  onClose: () => void;
}) {
  const remove = useDeleteDocument(kbId);

  async function confirm() {
    if (!doc) return;
    try {
      await remove.mutateAsync(doc.id);
      toast.success("Document deleted");
      onClose();
    } catch {
      toast.error("Failed to delete document");
    }
  }

  return (
    <AlertDialog open={!!doc} onOpenChange={(v) => !v && onClose()}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete document</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete{" "}
            <span className="font-medium text-foreground">{doc?.title}</span>?
            Its chunks will no longer be searchable. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={remove.isPending}
            onClick={(e) => {
              e.preventDefault();
              confirm();
            }}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {remove.isPending ? "Deleting…" : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
